import type { SiteCopy } from './types';

export const en: SiteCopy = {
  loading: {
    kicker: 'tap tap tap…',
    title: 'On it!',
    description: 'Getting your next page ready.',
  },
  common: {
    available: '20 h / week',
    downloadCv: 'Download CV',
    email: 'caishaojiang@gmail.com',
    github: 'https://github.com/agongdai',
    now: 'now',
    minutes: '{n} min',
    wip: 'Work in progress',
  },
  nav: {
    home: 'Home',
    projects: 'Work',
    articles: { storybook: 'Notes', nebula: 'Writing' },
    showcases: { storybook: 'Playground', nebula: 'Lab' },
    tools: 'Tools',
    cta: { storybook: 'Say hello', nebula: "Let's talk" },
    menu: 'Main',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    whereTo: 'where to?',
    switchDesign: 'Switch design',
    designHint: 'Same site, another look',
    designNudge: { storybook: 'psst… try another look!', nebula: 'New · restyle the whole site' },
    dismiss: 'Dismiss',
    toLight: 'Switch to light theme',
    toDark: 'Switch to dark theme',
    language: '中文',
    otherLanguage: 'Switch to Chinese',
    wallet: 'Wallet',
  },
  footer: {
    copyright: '© 2012 — 2026 Shaojiang Cai',
    place: { storybook: 'drawn with love in Zhangzhou', nebula: 'Zhangzhou, China' },
    privacy: 'Privacy',
    terms: 'Terms',
    roadmap: 'Roadmap',
    scrollTop: 'Back to top',
  },
  home: {
    skills: [
      { title: 'Product front-ends', text: 'React, Next.js and design systems that scale.' },
      { title: 'Trading & Web3', text: 'Exchanges, custody, DEX tools and wallets.' },
      { title: 'AI-native delivery', text: 'Agents that ship narrow, tested changes.' },
    ],
    clients: ['Binance', 'Bitfinex', 'CronosLabs', 'Pano AI', 'Toptal', 'EMBL-EBI', 'RingCentral'],
  },
  projects: {
    kicker: { storybook: '{n} little stories ↓', nebula: '{n} projects · {from} — {to}' },
    title: { storybook: "Things I've *made*", nebula: 'The *work.*' },
    lead: {
      storybook: '',
      nebula: 'Exchanges, custody, AI tools, and my own products.',
    },
    more: { storybook: 'Turn the page — {n} more', nebula: 'Show {n} earlier projects' },
    filterLabel: 'Filter projects',
    filters: {
      all: { storybook: 'Everything', nebula: 'All' },
      own: { storybook: 'My own', nebula: 'My products' },
      trading: 'Trading & DeFi',
      data: 'Data & AI',
      web: 'Web platforms',
    },
    view: 'View',
    grid: 'Grid',
    list: 'List',
    empty: 'Nothing here yet.',
  },
  project: {
    back: { storybook: 'All stories', nebula: 'All work' },
    story: 'story no. {n}',
    visit: 'Visit site',
    share: 'Share',
    copied: 'Link copied',
    role: 'Role',
    timeline: 'Timeline',
    company: 'Company',
    stack: 'Stack',
    part: 'Part {n}',
    features: 'Features',
    whatIDid: 'What I did',
    next: { storybook: 'next story →', nebula: 'Next project' },
    privateNote: 'Sign-in required',
    wip: {
      title: { storybook: 'Still *under construction*', nebula: 'Work in progress' },
      note: "I'm still building this one. Screens and features may change, and some of what you see below is an early preview.",
    },
    feature: 'Feature {n}',
    stats: {
      features: 'features',
      technologies: 'technologies',
      months: 'months on it',
      years: 'years on it',
    },
  },
  articles: {
    kicker: { storybook: 'Notes', nebula: 'Writing · {n} articles' },
    title: { storybook: 'Notes from *the work*', nebula: 'Field *notes.*' },
    lead: { storybook: 'Short, practical write-ups. {n} so far.', nebula: '' },
    search: 'Search',
    searchPlaceholder: 'nginx, jest, next.js…',
    filterLabel: 'Filter by topic',
    all: 'All',
    featured: 'Featured',
    empty: 'No articles match.',
  },
  article: {
    back: { storybook: 'Notes', nebula: 'Writing' },
    onThisPage: { storybook: 'In this note', nebula: 'On this page' },
    ping: 'questions? ping me!',
    previous: '← previous',
    next: 'next →',
    keepReading: 'Keep reading',
    copyLink: 'Copy link',
    demoRepo: 'Demo repo',
  },
  showcases: {
    kicker: { storybook: 'come play ↓', nebula: 'The lab · {n} showcases' },
    title: { storybook: 'The *playground*', nebula: 'Experiments *you can touch.*' },
    lead: {
      storybook: '{n} little experiments and two free tools.',
      nebula: 'Small, polished front-end pieces. Open one and play.',
    },
    tools: 'Tools',
    toolsNote: { storybook: 'free, forever', nebula: 'Free · runs in your browser' },
    items: {
      panorama: { title: 'Panorama viewer', line: 'Drag around a 360° photo.' },
      'audio-player': { title: 'Audio player', line: 'Live sound visualizer.' },
      'fancy-border': { title: 'Fancy border', line: 'Animated conic border.' },
      'flowing-dashed-lines': { title: 'Flowing dashed lines', line: 'SVG data flow.' },
      form: { title: 'Inventory form', line: 'RHF + Zod, conditional fields.' },
      enterprise: { title: 'Enterprise landing', line: 'Pixel-perfect marketing page.' },
    },
  },
  tools: {
    kicker: { storybook: 'a free little tool', nebula: 'Tools' },
    colorPicker: {
      title: 'Color *picker*',
      line: 'HEX · RGB · HSL, shades and contrast.',
      lead: 'Pick a color, copy it in any format.',
    },
    svg2base64: {
      title: 'SVG → *Base64*',
      line: 'Paste an SVG, get a data URI.',
      lead: 'Paste or drop an SVG and copy it as a data URI.',
    },
    hue: 'Hue',
    saturation: 'Saturation',
    lightness: 'Lightness',
    hex: 'Type a color',
    shades: 'Shades · click to pick',
    presets: { storybook: 'Paint box', nebula: 'Presets' },
    contrast: 'Contrast {n} : 1',
    copy: 'Copy',
    copied: 'Copied',
  },
};
